<template>
  <div
    v-if="showTicketsCreadosResueltos || showTiempoPromedio || showTicketsPorTecnico || showTicketsPorArea || showProblemasComunes || showEstadisticasPreventivas"
    class="stats-dashboard"
  >
    <!-- Vista 1: Total de tickets creados y resueltos -->
    <div v-if="showTicketsCreadosResueltos" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Tickets - Creados y Resueltos</h2>
          <button class="close-btn" @click="cerrarTicketsCreadosResueltos">✕</button>
        </div>
        <div class="stats-content">

          <!-- Filtros de fecha específica -->
          <div class="date-filters">
            <div class="date-filter-group">
              <label for="fechaInicio">Fecha Inicio:</label>
              <input 
                type="date" 
                id="fechaInicio" 
                v-model="fechaInicio" 
                @change="actualizarDatosPorFecha"
                class="date-input"
              />
            </div>
            <div class="date-filter-group">
              <label for="fechaFin">Fecha Fin:</label>
              <input 
                type="date" 
                id="fechaFin" 
                v-model="fechaFin" 
                @change="actualizarDatosPorFecha"
                class="date-input"
              />
            </div>
            <div class="date-filter-group">
              <label for="añoSelect">Año:</label>
              <select 
                id="añoSelect" 
                v-model="añoSeleccionado" 
                @change="actualizarDatosPorAño"
                class="year-select"
              >
                <option value="">Seleccionar año</option>
                <option v-for="año in añosDisponibles" :key="año" :value="año">
                  {{ año }}
                </option>
              </select>
            </div>
            <div class="date-filter-group">
              <label for="mesSelect">Mes:</label>
              <select 
                id="mesSelect" 
                v-model="mesSeleccionado" 
                @change="actualizarDatosPorMes"
                class="month-select"
                :disabled="!añoSeleccionado"
              >
                <option value="">Seleccionar mes</option>
                <option v-for="(mes, index) in meses" :key="index" :value="index + 1">
                  {{ mes }}
                </option>
              </select>
            </div>
            <button @click="limpiarFiltros" class="clear-filters-btn">
              Limpiar Filtros
            </button>
          </div>
          
          <!-- Estadísticas principales -->
          <div class="stats-summary">
            <div class="stat-card created">
              <div class="stat-number">{{ ticketsCreados }}</div>
              <div class="stat-label">Tickets Asignados</div>
            </div>
            <div class="stat-card resolved">
              <div class="stat-number">{{ ticketsResueltos }}</div>
              <div class="stat-label">Tickets Resueltos</div>
            </div>
            <div class="stat-card pending">
              <div class="stat-number">{{ ticketsPendientes }}</div>
              <div class="stat-label">Tickets Pendientes</div>
            </div>
            <div class="stat-card rate">
              <div class="stat-number">{{ tasaResolucion }}%</div>
              <div class="stat-label">Tasa de Resolución</div>
            </div>
          </div>

          <!-- Gráfico de dispersión (scatter) -->
          <div class="chart-container scatter">
            <h4>Evolución por {{ periodoActivoTexto }}</h4>
            <div class="scatter-chart">
              <div class="chart-axis-label axis-y">Tickets</div>
              <div
                v-for="(item, index) in datosGraficoNormalizados"
                :key="item.etiqueta"
                class="scatter-point"
                :style="{ left: ((index + 1) * (100 / (datosGraficoNormalizados.length + 1))) + '%', bottom: item.altura + '%' }"
              >
                <div :class="['dot', `dot-${item.className}`]"></div>
                <div class="scatter-label">{{ item.etiqueta }}</div>
              </div>
              <div class="chart-axis-label axis-x">{{ periodoActivoTexto }}</div>
            </div>
          </div>
        </div>
        <div class="stats-actions">
          <button class="download-btn" @click="descargarReporteTickets">
            <span class="download-icon">📄</span> Descargar Reporte
          </button>
          <button @click="cerrarTicketsCreadosResueltos">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 2: Tiempo promedio de resolución -->
    <div v-if="showTiempoPromedio" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Tiempo Promedio de Resolución</h2>
          <button class="close-btn" @click="cerrarTiempoPromedio">✕</button>
        </div>
        <div class="stats-content">
          <div class="time-stats">
            <div class="time-card general">
              <div class="time-icon"></div>
              <div class="time-info">
                <div class="time-label">Tiempo General</div>
                <div class="time-value">{{ tiempoPromedioGeneral }}</div>
                <div class="time-tickets">{{ totalTicketsProcesados }} tickets</div>
              </div>
            </div>
            <div class="time-card high">
              <div class="time-icon"></div>
              <div class="time-info">
                <div class="time-label">Prioridad Alta</div>
                <div class="time-value">{{ tiempoAlta }}</div>
                <div class="time-tickets">{{ ticketsAlta }} tickets</div>
              </div>
            </div>
            <div class="time-card medium">
              <div class="time-icon"></div>
              <div class="time-info">
                <div class="time-label">Prioridad Media</div>
                <div class="time-value">{{ tiempoMedia }}</div>
                <div class="time-tickets">{{ ticketsMedia }} tickets</div>
              </div>
            </div>
            <div class="time-card low">
              <div class="time-icon"></div>
              <div class="time-info">
                <div class="time-label">Prioridad Baja</div>
                <div class="time-value">{{ tiempoBaja }}</div>
                <div class="time-tickets">{{ ticketsBaja }} tickets</div>
              </div>
            </div>
          </div>
          
          <!-- Gráfico de tiempo por área -->
          <div class="time-chart">
            <h4>Tiempo Promedio por Área</h4>
            <div class="time-bars">
              <div class="chart-axis-label axis-y">Tiempo</div>
              <div v-for="area in tiempoPorArea" :key="area.nombre" class="time-bar-item">
                <div class="time-bar-label">{{ area.nombre }}</div>
                <div class="time-bar-container">
                  <div class="time-bar-fill" :style="{ width: area.porcentaje + '%' }"></div>
                  <span class="time-bar-value">{{ area.tiempo }}</span>
                </div>
              </div>
              <div class="chart-axis-label axis-x">Áreas</div>
            </div>
          </div>

          <!-- Gráfico de barras por prioridad -->
          <div class="priority-chart scatter">
            <h4>Tiempo Promedio por Prioridad</h4>
            <div class="priority-scatter-chart">
              <div class="chart-axis-label axis-y">Tiempo</div>
              <div
                v-for="(item, index) in prioridadChart"
                :key="item.label"
                class="priority-scatter-point"
                :style="{ left: ((index + 1) * (100 / (prioridadChart.length + 1))) + '%', bottom: item.horas / (maxHorasPrioridad || 1) * 100 + '%' }"
              >
                <div :class="['dot', 'dot-' + item.className]"></div>
                <div class="scatter-label">{{ item.label }}</div>
              </div>
              <div class="chart-axis-label axis-x">Prioridad</div>
            </div>
          </div>
        </div>
        <div class="stats-actions">
          <button class="download-btn" @click="descargarReporteTiempoPromedio">
            <span class="download-icon">📄</span> Descargar Reporte
          </button>
          <button class="download-btn dictamen-btn" @click="descargarDictamenMensual">
            <span class="download-icon">📋</span> Descargar Dictamen Mensual
          </button>
          <button @click="cerrarTiempoPromedio">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 3: Tickets atendidos por técnicos -->
    <div v-if="showTicketsPorTecnico" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Tickets por Técnicos</h2>
          <button class="close-btn" @click="cerrarTicketsPorTecnico">✕</button>
        </div>
        <div class="stats-content">
          <div class="stats-summary">
            <div class="stat-card">
              <div class="stat-number">{{ totalTecnicos }}</div>
              <div class="stat-label">Técnicos Activos</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ totalTicketsAtendidos }}</div>
              <div class="stat-label">Total Tickets Atendidos</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ promedioPorTecnico }}</div>
              <div class="stat-label">Promedio por Técnico</div>
            </div>
          </div>
          
          <!-- Gráfico de barras por técnico -->
          <div class="tech-chart">
            <h4>Rendimiento por Técnico</h4>
            <div class="tech-bars">
              <div v-for="tecnico in tecnicosData" :key="tecnico.nombre" class="tech-bar-item">
                <div class="tech-bar-label">{{ tecnico.nombre }}</div>
                <div class="tech-bar-container">
                  <div class="tech-bar-fill" :style="{ width: (tecnico.atendidos / 35 * 100) + '%' }"></div>
                  <span class="tech-bar-value">{{ tecnico.atendidos }} tickets</span>
                </div>
              </div>
            </div>
          </div>

          <div class="tech-table">
            <table>
              <thead>
                <tr>
                  <th>Técnico</th>
                  <th>Tickets Atendidos</th>
                  <th>Tickets Resueltos</th>
                  <th>Tickets Pendientes</th>
                  <th>Tiempo Promedio</th>
                  <th>Eficiencia</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="tecnico in tecnicosData" :key="tecnico.nombre">
                  <td class="tech-name">
                    <div class="tech-avatar">{{ tecnico.iniciales }}</div>
                    {{ tecnico.nombre }}
                  </td>
                  <td>{{ tecnico.atendidos }}</td>
                  <td>{{ tecnico.resueltos }}</td>
                  <td>{{ tecnico.pendientes }}</td>
                  <td>{{ tecnico.tiempoPromedio }}</td>
                  <td>
                    <span :class="['efficiency', getEfficiencyClass(tecnico.eficiencia)]">
                      {{ tecnico.eficiencia }}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="stats-actions">
          <button class="download-btn" @click="descargarReporteTecnicos">
            <span class="download-icon">📄</span> Descargar Reporte
          </button>
          <button @click="cerrarTicketsPorTecnico">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 4: Tickets por área solicitada -->
    <div v-if="showTicketsPorArea" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Tickets por Área</h2>
          <button class="close-btn" @click="cerrarTicketsPorArea">✕</button>
        </div>
        <div class="stats-content">
          <!-- Filtros de fecha para reporte por área -->
          <div class="date-filters">
            <div class="date-filter-group">
              <label for="fechaInicioAreas">Fecha Inicio:</label>
              <input 
                type="date" 
                id="fechaInicioAreas" 
                v-model="fechaInicio" 
                @change="actualizarDatosAreas"
                class="date-input"
              />
            </div>
            <div class="date-filter-group">
              <label for="fechaFinAreas">Fecha Fin:</label>
              <input 
                type="date" 
                id="fechaFinAreas" 
                v-model="fechaFin" 
                @change="actualizarDatosAreas"
                class="date-input"
              />
            </div>
            <button @click="limpiarFiltrosAreas" class="clear-filters-btn">
              Limpiar Filtros
            </button>
          </div>
          <!-- Cards con indicador diario -->
          <div class="area-stats">
            <div class="area-card biomedica">
              <div class="area-icon"></div>
              <div class="area-info">
                <div class="area-name">Biomédica</div>
                <div class="area-tickets">{{ ticketsBiomedica }}</div>
                <div class="area-percentage">{{ porcentajeBiomedica }}%</div>
                <div class="area-daily-indicator">
                  <span class="daily-label">Promedio Diario:</span>
                  <span class="daily-value">{{ ticketsBiomedicaDiario }} tickets/día</span>
                  <span :class="['daily-trend', getTrendClass(ticketsBiomedicaTendencia)]">
                    {{ getTrendArrow(ticketsBiomedicaTendencia) }}
                    {{ Math.abs(ticketsBiomedicaTendencia) }}%
                  </span>
                </div>
              </div>
            </div>
            <div class="area-card tecnologias">
              <div class="area-icon"></div>
              <div class="area-info">
                <div class="area-name">Tecnologías</div>
                <div class="area-tickets">{{ ticketsTecnologias }}</div>
                <div class="area-percentage">{{ porcentajeTecnologias }}%</div>
                <div class="area-daily-indicator">
                  <span class="daily-label">Promedio Diario:</span>
                  <span class="daily-value">{{ ticketsTecnologiasDiario }} tickets/día</span>
                  <span :class="['daily-trend', getTrendClass(ticketsTecnologiasTendencia)]">
                    {{ getTrendArrow(ticketsTecnologiasTendencia) }}
                    {{ Math.abs(ticketsTecnologiasTendencia) }}%
                  </span>
                </div>
              </div>
            </div>
            <div class="area-card mantenimiento">
              <div class="area-icon"></div>
              <div class="area-info">
                <div class="area-name">Mantenimiento</div>
                <div class="area-tickets">{{ ticketsMantenimiento }}</div>
                <div class="area-percentage">{{ porcentajeMantenimiento }}%</div>
                <div class="area-daily-indicator">
                  <span class="daily-label">Promedio Diario:</span>
                  <span class="daily-value">{{ ticketsMantenimientoDiario }} tickets/día</span>
                  <span :class="['daily-trend', getTrendClass(ticketsMantenimientoTendencia)]">
                    {{ getTrendArrow(ticketsMantenimientoTendencia) }}
                    {{ Math.abs(ticketsMantenimientoTendencia) }}%
                  </span>
                </div>
              </div>
            </div>
          </div>


          <div class="area-monthly-bars-chart">
            <h4>Tickets por Área - Últimos 6 Meses</h4>
            <div class="vertical-monthly-chart">
              <div class="chart-axis-label axis-y">Tickets</div>
              <div class="vertical-monthly-chart-bars">
                <div v-for="mes in ticketsPorMesChart" :key="mes.etiqueta" class="vertical-month-group">
                  <div class="vertical-bars">
                    <div class="vertical-bar biomedica" :style="{ height: mes.alturaBiomedica + '%' }" :title="'Biomédica: ' + mes.biomedica"></div>
                    <div class="vertical-bar tecnologias" :style="{ height: mes.alturaTecnologias + '%' }" :title="'Tecnologías: ' + mes.tecnologias"></div>
                    <div class="vertical-bar mantenimiento" :style="{ height: mes.alturaMantenimiento + '%' }" :title="'Mantenimiento: ' + mes.mantenimiento"></div>
                  </div>
                  <div class="vertical-month-label">{{ mes.etiqueta }}</div>
                </div>
              </div>
              <div class="chart-axis-label axis-x">Mes y año</div>
            </div>
            <div class="monthly-chart-legend">
              <div class="legend-item">
                <div class="legend-color biomedica"></div>
                <span>Biomédica</span>
              </div>
              <div class="legend-item">
                <div class="legend-color tecnologias"></div>
                <span>Tecnologías</span>
              </div>
              <div class="legend-item">
                <div class="legend-color mantenimiento"></div>
                <span>Mantenimiento</span>
              </div>
            </div>
          </div>

          <!-- Tabla detallada -->
          <div class="area-table">
            <table>
              <thead>
                <tr>
                  <th>Área</th>
                  <th>Total Tickets</th>
                  <th>Resueltos</th>
                  <th>Pendientes</th>
                  <th>Tiempo Promedio</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="area in areasDetalladas" :key="area.nombre">
                  <td class="area-name-cell">
                    <span :class="['area-icon-small', area.clase]">{{ area.icono }}</span>
                    {{ area.nombre }}
                  </td>
                  <td>{{ area.total }}</td>
                  <td>{{ area.resueltos }}</td>
                  <td>{{ area.pendientes }}</td>
                  <td>{{ area.tiempoPromedio }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="stats-actions">
          <button class="download-btn" @click="descargarReporteAreas">
            <span class="download-icon">📄</span> Descargar Reporte
          </button>
          <button @click="cerrarTicketsPorArea">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 5: Problemas más comunes -->
    <div v-if="showProblemasComunes" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Problemas Más Comunes</h2>
          <button class="close-btn" @click="cerrarProblemasComunes">✕</button>
        </div>
        <div class="stats-content">
          <!-- Resumen general -->
          <div class="problemas-summary">
            <div class="stat-card">
              <div class="stat-number">{{ problemasComunes.length }}</div>
              <div class="stat-label">Tipos de Problemas</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ totalProblemasAnalizados }}</div>
              <div class="stat-label">Total de Tickets Analizados</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ problemasComunes.length > 0 ? Math.max(...problemasComunes.map(p => p.frecuencia)) : 0 }}</div>
              <div class="stat-label">Frecuencia Máxima</div>
            </div>
          </div>

          <!-- Gráfico de barras horizontales de problemas comunes -->
          <div class="problemas-chart">
            <h4>Problemas Más Frecuentes</h4>
            <div class="problemas-bars">
              <div v-for="problema in problemasComunes.slice(0, 10)" :key="problema.id" class="problema-bar-item">
                <div class="problema-info">
                  <div class="problema-descripcion">{{ problema.descripcion }}</div>
                  <div class="problema-meta">
                    <span class="problema-area">{{ problema.area }}</span>
                    <span :class="['problema-prioridad', getPrioridadClass(problema.prioridad)]">
                      {{ problema.prioridad }}
                    </span>
                  </div>
                </div>
                <div class="problema-bar-container">
                  <div class="problema-bar-fill" :style="{ width: problema.porcentaje + '%' }"></div>
                  <div class="problema-bar-stats">
                    <span class="problema-frecuencia">{{ problema.frecuencia }}</span>
                    <span class="problema-porcentaje">{{ problema.porcentaje }}%</span>
                  </div>
                </div>
                <div class="problema-tendencia">
                  <span :class="['tendencia', getTrendClass(problema.tendencia)]">
                    {{ getTrendArrow(problema.tendencia) }} {{ Math.abs(problema.tendencia) }}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabla detallada -->
          <div class="problemas-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Problema</th>
                  <th>Área</th>
                  <th>Frecuencia</th>
                  <th>Prioridad</th>
                  <th>Tendencia</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="problema in problemasComunes" :key="problema.id">
                  <td class="problema-rank">{{ problema.id }}</td>
                  <td class="problema-descripcion-cell">{{ problema.descripcion }}</td>
                  <td class="problema-area-cell">{{ problema.area }}</td>
                  <td class="problema-frecuencia-cell">
                    <strong>{{ problema.frecuencia }}</strong>
                    <span class="porcentaje-chica">({{ problema.porcentaje }}%)</span>
                  </td>
                  <td>
                    <span :class="['prioridad-badge', getPrioridadClass(problema.prioridad)]">
                      {{ problema.prioridad }}
                    </span>
                  </td>
                  <td>
                    <span :class="['tendencia-badge', getTrendClass(problema.tendencia)]">
                      {{ getTrendArrow(problema.tendencia) }} {{ Math.abs(problema.tendencia) }}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="stats-actions">
          <button class="download-btn" @click="descargarReporteProblemas">
            <span class="download-icon">📄</span> Descargar Reporte
          </button>
          <button @click="cerrarProblemasComunes">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 6: Estadísticas preventivas (áreas solicitantes + fallas comunes) -->
    <div v-if="showEstadisticasPreventivas" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Estadísticas Preventivas</h2>
          <button class="close-btn" @click="cerrarEstadisticasPreventivas">✕</button>
        </div>
        <div class="stats-content">
          <div class="date-filters">
            <div class="date-filter-group">
              <label for="fechaInicioPrevent">Fecha Inicio:</label>
              <input
                type="date"
                id="fechaInicioPrevent"
                v-model="fechaInicio"
                @change="cargarEstadisticasPreventivas"
                class="date-input"
              />
            </div>
            <div class="date-filter-group">
              <label for="fechaFinPrevent">Fecha Fin:</label>
              <input
                type="date"
                id="fechaFinPrevent"
                v-model="fechaFin"
                @change="cargarEstadisticasPreventivas"
                class="date-input"
              />
            </div>
            <button @click="resetRangoPreventivo" class="clear-filters-btn">
              Últimos 30 días
            </button>
          </div>

          <div class="stats-summary">
            <div class="stat-card created">
              <div class="stat-number">{{ areasSolicitantes.length }}</div>
              <div class="stat-label">Áreas Solicitantes</div>
            </div>
            <div class="stat-card pending">
              <div class="stat-number">{{ totalTicketsPreventivos }}</div>
              <div class="stat-label">Tickets Analizados</div>
            </div>
            <div class="stat-card resolved">
              <div class="stat-number">{{ problemasComunesPreventivos.length }}</div>
              <div class="stat-label">Tipos de Fallas</div>
            </div>
            <div class="stat-card rate">
              <div class="stat-number">{{ areasSolicitantes.length > 0 ? areasSolicitantes[0].conteo : 0 }}</div>
              <div class="stat-label">Máx. por Área</div>
            </div>
          </div>

          <div v-if="prediccionesPreventivas && prediccionesPreventivas.length" class="predict-grid">
            <div
              v-for="(p, idx) in prediccionesPreventivas"
              :key="idx"
              class="predict-card"
              :class="getPredictCardClass(p)"
            >
              <div class="predict-header">
                <div class="predict-left">
                  <div class="predict-icon" aria-hidden="true">⚠️</div>
                  <div class="predict-header-text">
                    <div class="predict-title">Evento predictivo</div>
                    <div class="predict-subtitle">Recomendación preventiva</div>
                  </div>
                </div>
                <div class="predict-right">
                  <span class="predict-badge" :class="getPredictBadgeClass(p)">
                    {{ getPredictBadgeText(p) }}
                  </span>
                </div>
              </div>

              <div class="predict-body">
                <div class="predict-text">{{ p.texto }}</div>
                <div class="predict-meta">
                  <div class="predict-meta-item">
                    <span class="predict-meta-label">Problema</span>
                    <span class="predict-meta-value">{{ p.falla }}</span>
                  </div>
                  <div class="predict-meta-item">
                    <span class="predict-meta-label">Área</span>
                    <span class="predict-meta-value">{{ p.area }}</span>
                  </div>
                  <div class="predict-meta-item">
                    <span class="predict-meta-label">Frecuencia</span>
                    <span class="predict-meta-value">{{ p.frecuencia }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="prevent-grid">
            <div class="prevent-card">
              <h4>Áreas con más tickets (solicitantes)</h4>
              <div class="prevent-table">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Área solicitante</th>
                      <th>Tickets</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(a, idx) in areasSolicitantes" :key="a.area">
                      <td>{{ idx + 1 }}</td>
                      <td class="prevent-area">{{ a.area }}</td>
                      <td><strong>{{ a.conteo }}</strong></td>
                      <td>{{ a.porcentaje }}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="prevent-card">
              <h4>Fallas más comunes (por título)</h4>
              <div class="prevent-table">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Falla</th>
                      <th>Área técnica</th>
                      <th>Frecuencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, idx) in problemasComunesPreventivos" :key="idx">
                      <td>{{ idx + 1 }}</td>
                      <td class="prevent-problema">{{ p.descripcion }}</td>
                      <td>{{ p.area }}</td>
                      <td><strong>{{ p.frecuencia }}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
        <div class="stats-actions">
          <button class="download-btn" @click="descargarReportePreventivo">
            <span class="download-icon">📄</span> Descargar Reporte
          </button>
          <button @click="cerrarEstadisticasPreventivas">Cerrar</button>
        </div>
      </div>
    </div>
    </div>
</template>

<script>
export default {
    name: 'TicketStatsModal',
  props: {
    tickets: {
      type: Array,
      default: () => []
    },
    showTicketsCreadosResueltos: {
      type: Boolean,
      default: false
    },
    showTiempoPromedio: {
      type: Boolean,
      default: false
    },
    showTicketsPorTecnico: {
      type: Boolean,
      default: false
    },
    showTicketsPorArea: {
      type: Boolean,
      default: false
    },
    showProblemasComunes: {
      type: Boolean,
      default: false
    },
    showEstadisticasPreventivas: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Modal 1: Tickets creados y resueltos
      periodoActivo: 'dia',
      periodos: [
        { id: 'dia', nombre: 'Por Día' },
        { id: 'semana', nombre: 'Por Semana' },
        { id: 'año', nombre: 'Por Año' }
      ],
      // Filtros de fecha
      fechaInicio: '',
      fechaFin: '',
      añoSeleccionado: '',
      mesSeleccionado: '',
      añosDisponibles: [2023, 2024, 2025],
      meses: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ],
      ticketsCreados: 0,
      ticketsResueltos: 0,
      ticketsPendientes: 0,
      tasaResolucion: 0,
      datosGrafico: [],

      // Modal 2: Tiempo promedio de resolución
      tiempoPromedioGeneral: '0 horas',
      tiempoPromedioHoras: 0,
      totalTicketsProcesados: 0,
      tiempoAlta: '0 horas',
      ticketsAlta: 0,
      tiempoMedia: '0 horas',
      ticketsMedia: 0,
      tiempoBaja: '0 horas',
      ticketsBaja: 0,
      tiempoPorArea: [],
      tiempoAltaHoras: 0,
      tiempoMediaHoras: 0,
      tiempoBajaHoras: 0,

      // Modal 3: Tickets por técnico
      totalTecnicos: 0,
      totalTicketsAtendidos: 0,
      promedioPorTecnico: 0,
      tecnicosData: [],

      // Modal 4: Tickets por área
      ticketsBiomedica: 0,
      ticketsTecnologias: 0,
      ticketsMantenimiento: 0,
      porcentajeBiomedica: 0,
      porcentajeTecnologias: 0,
      porcentajeMantenimiento: 0,
      
      // Indicadores diarios por área
      ticketsBiomedicaDiario: 0,
      ticketsTecnologiasDiario: 0,
      ticketsMantenimientoDiario: 0,
      ticketsBiomedicaTendencia: 0,
      ticketsTecnologiasTendencia: 0,
      ticketsMantenimientoTendencia: 0,
      
      // Datos mensuales por área (últimos 6 meses)
      ticketsPorMes: [],
      
      // Modal 5: Problemas más comunes
      problemasComunes: [],
      totalProblemasAnalizados: 0,

      // Modal 6: Preventivas
      areasSolicitantes: [],
      problemasComunesPreventivos: [],
      totalTicketsPreventivos: 0,
      prediccionesPreventivas: [],
      
      areasDetalladas: []
    }
  },
  computed: {
    periodoActivoTexto() {
      const periodo = this.periodos.find(p => p.id === this.periodoActivo);
      return periodo ? periodo.nombre : 'Por Día';
    },
    datosGraficoNormalizados() {
      if (!Array.isArray(this.datosGrafico) || this.datosGrafico.length === 0) {
        return [];
      }

      const valores = this.datosGrafico.map(item => Number(item.valor) || 0);
      const maxValor = valores.length ? Math.max(...valores) : 0;
      const divisor = maxValor > 0 ? maxValor : 1;
      const alturaMinima = 18;

      return this.datosGrafico.map(item => {
        const valor = Number(item.valor) || 0;
        const altura = valor > 0 ? Math.max(alturaMinima, (valor / divisor) * 100) : 0;
        return {
          ...item,
          valor,
          altura
        };
      });
    },
    prioridadChart() {
      const valores = [
        {
          label: 'Alta',
          horas: this.tiempoAltaHoras || 0,
          valorTexto: this.tiempoAlta,
          className: 'high',
          tickets: this.ticketsAlta || 0
        },
        {
          label: 'Media',
          horas: this.tiempoMediaHoras || 0,
          valorTexto: this.tiempoMedia,
          className: 'medium',
          tickets: this.ticketsMedia || 0
        },
        {
          label: 'Baja',
          horas: this.tiempoBajaHoras || 0,
          valorTexto: this.tiempoBaja,
          className: 'low',
          tickets: this.ticketsBaja || 0
        }
      ];
      const maxHoras = Math.max(...valores.map(v => v.horas), 0);
      const divisor = maxHoras > 0 ? maxHoras : 1;
      return valores.map(v => ({
        ...v,
        height: v.horas > 0
          ? Math.min(100, Math.max(10, (v.horas / divisor) * 100))
          : v.tickets > 0
            ? 10
            : 0
      }));
    },
    maxHorasPrioridad() {
      const horas = this.prioridadChart.map(v => v.horas || 0);
      return horas.length ? Math.max(...horas) : 0;
    },
    ticketsPorMesChart() {
      if (!Array.isArray(this.ticketsPorMes) || this.ticketsPorMes.length === 0) {
        return [];
      }

      const maxTotal = Math.max(...this.ticketsPorMes.map(m => Number(m.total || 0)), 0);
      const divisor = maxTotal > 0 ? maxTotal : 1;

      return this.ticketsPorMes.map(mes => {
        const biomedica = Number(mes.biomedica || 0);
        const tecnologias = Number(mes.tecnologias || 0);
        const mantenimiento = Number(mes.mantenimiento || 0);
        return {
          ...mes,
          etiqueta: mes.etiqueta || mes.mes,
          biomedica,
          tecnologias,
          mantenimiento,
          alturaBiomedica: (biomedica / divisor) * 100,
          alturaTecnologias: (tecnologias / divisor) * 100,
          alturaMantenimiento: (mantenimiento / divisor) * 100
        };
      });
    }
  },
  watch: {
    // Cuando se abre el modal de tickets creados/resueltos, cargar datos
    showTicketsCreadosResueltos(newVal) {
      if (newVal) {
        this.cargarEstadisticasDesdeBackend();
      }
    },
    // Cuando se abre el modal de tiempo promedio, cargar datos
    showTiempoPromedio(newVal) {
      if (newVal) {
        this.cargarEstadisticasDesdeBackend();
      }
    },
    // Cuando se abre el modal de tickets por técnico, cargar datos
    showTicketsPorTecnico(newVal) {
      if (newVal) {
        this.cargarEstadisticasTecnicosDesdeBackend();
      }
    },
    // Cuando se abre el modal de tickets por área, cargar datos
    showTicketsPorArea(newVal) {
      if (newVal) {
        this.cargarEstadisticasDesdeBackend();
      }
    },
    // Cuando se abre el modal de problemas comunes, cargar datos
    showProblemasComunes(newVal) {
      if (newVal) {
        this.ensureDefaultDateRange();
        this.cargarProblemasComunes();
      }
    },
    showEstadisticasPreventivas(newVal) {
      if (newVal) {
        this.ensureDefaultDateRange();
        this.cargarEstadisticasPreventivas();
      }
    }
  },
  methods: {
    getPredictLevel(p) {
      const freq = Number(p?.frecuencia || 0) || 0;
      if (freq >= 5) return 'high';
      if (freq >= 3) return 'medium';
      return 'low';
    },
    getPredictBadgeText(p) {
      const level = this.getPredictLevel(p);
      if (level === 'high') return 'ALTA';
      if (level === 'medium') return 'MEDIA';
      return 'BAJA';
    },
    getPredictBadgeClass(p) {
      const level = this.getPredictLevel(p);
      return `predict-badge-${level}`;
    },
    getPredictCardClass(p) {
      const level = this.getPredictLevel(p);
      return `predict-card-${level}`;
    },
    ensureDefaultDateRange() {
      if (this.fechaInicio && this.fechaFin) return;
      const fin = new Date();
      const inicio = new Date();
      inicio.setDate(inicio.getDate() - 30);
      this.fechaFin = this.formatearFechaInput(fin);
      this.fechaInicio = this.formatearFechaInput(inicio);
    },
    formatearFechaInput(date) {
      const d = date instanceof Date ? date : new Date(date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    resetRangoPreventivo() {
      this.fechaInicio = '';
      this.fechaFin = '';
      this.ensureDefaultDateRange();
      this.cargarEstadisticasPreventivas();
    },

    // Métodos para filtros del panel de áreas
    actualizarDatosAreas() {
      this.cargarEstadisticasDesdeBackend();
    },

    limpiarFiltrosAreas() {
      this.fechaInicio = '';
      this.fechaFin = '';
      this.cargarEstadisticasDesdeBackend();
    },

    async cargarEstadisticasPreventivas() {
      try {
        this.ensureDefaultDateRange();
        const response = await this.$http({
          url: '/mantenimiento/tickets/estadisticasPreventivas',
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          params: {
            fechaInicio: this.fechaInicio || undefined,
            fechaFin: this.fechaFin || undefined
          }
        });

        const data = response.data || {};
        if (data && data.error) {
          this.$emit('mostrar-notificacion', data.mensaje || 'No se pudieron obtener estadísticas preventivas');
          return;
        }

        const root = data.root || {};
        this.areasSolicitantes = Array.isArray(root.areasSolicitantes) ? root.areasSolicitantes : [];
        this.totalTicketsPreventivos = Number(root.totalTicketsAnalizados || 0) || 0;
        this.problemasComunesPreventivos = Array.isArray(root.problemasComunes) 
          ? root.problemasComunes.filter(p => p != null) 
          : [];

        const fallasTopPorArea = Array.isArray(root.fallasTopPorArea) ? root.fallasTopPorArea : [];
        this.prediccionesPreventivas = fallasTopPorArea
          .filter(x => x && x.area && x.falla && x.frecuencia)
          .map(x => {
            const area = (x.area || '').toString().trim();
            const falla = (x.falla || '').toString().trim();
            const frecuencia = Number(x.frecuencia || 0) || 0;
            return {
              area,
              falla,
              frecuencia,
              texto: `Revisar ${falla} en ${area} por posible falla.`
            };
          });
      } catch (error) {
        this.$emit('mostrar-notificacion', 'Error al cargar estadísticas preventivas');
      }
    },
    contarTicketsLocalParaTecnico(tecnico) {
      const resultado = {
        atendidos: 0,
        resueltos: 0,
        pendientes: 0
      };

      if (!tecnico || (tecnico.id === undefined && !tecnico.nombre && !tecnico.nombres)) {
        return resultado;
      }
      if (!Array.isArray(this.tickets) || this.tickets.length === 0) {
        return resultado;
      }

      const tecnicoId = tecnico.id;
      const tecnicoNombre = (tecnico.nombre || `${tecnico.nombres || ''} ${tecnico.apellidoPaterno || ''}`.trim()).trim();
      const tecnicoNombreUpper = tecnicoNombre ? tecnicoNombre.toUpperCase() : '';

      const ticketMatch = (ticket) => {
        if (!ticket) return false;

        const idAsignado = ticket.tecnicoAsignadoId ?? ticket.tecnicoId ?? ticket.tecnicoAsignado?.id;
        if (tecnicoId !== undefined && tecnicoId !== null && tecnicoId !== '' && idAsignado !== undefined && idAsignado !== null && idAsignado !== '') {
          if (Number(idAsignado) === Number(tecnicoId)) return true;
        }

        if (tecnicoNombre) {
          const tecnicosEquipo = Array.isArray(ticket.tecnicosEquipo) ? ticket.tecnicosEquipo : [];
          const encontradoEnEquipo = tecnicosEquipo.some(item => {
            if (!item) return false;

            // Caso 1: viene como objeto usuario
            if (typeof item === 'object') {
              const idItem = item.id ?? item.usuarioId ?? item.tecnicoId;
              if (tecnicoId !== undefined && tecnicoId !== null && idItem !== undefined && idItem !== null) {
                if (Number(idItem) === Number(tecnicoId)) return true;
              }
              const nombreItem = (item.nombre || `${item.nombres || ''} ${item.apellidoPaterno || ''}`.trim()).trim();
              if (nombreItem && tecnicoNombreUpper && nombreItem.toUpperCase().includes(tecnicoNombreUpper)) return true;
            }

            // Caso 2: viene como string
            const texto = item.toString();
            return tecnicoNombreUpper && texto.toUpperCase().includes(tecnicoNombreUpper);
          });
          if (encontradoEnEquipo) return true;

          const equipoNombres = (ticket.equipoNombres || '').toString();
          if (equipoNombres && tecnicoNombreUpper && equipoNombres.toUpperCase().includes(tecnicoNombreUpper)) return true;

          const tecnicoTexto = (ticket.tecnico || ticket.tecnicoAsignado || '').toString();
          if (tecnicoTexto && tecnicoNombreUpper && tecnicoTexto.toUpperCase().includes(tecnicoNombreUpper)) return true;

          const ultimoComentario = (ticket.ultimoComentario || '').toString();
          if (ultimoComentario && tecnicoNombreUpper && ultimoComentario.toUpperCase().includes(tecnicoNombreUpper)) return true;
        }

        return false;
      };

      const ticketsDelTecnico = this.tickets.filter(ticketMatch);
      resultado.atendidos = ticketsDelTecnico.length;
      resultado.resueltos = ticketsDelTecnico.filter(t => (t.estado || '').toString().toUpperCase() === 'RESUELTO').length;
      resultado.pendientes = Math.max(resultado.atendidos - resultado.resueltos, 0);
      return resultado;
    },

    // Métodos para cerrar modales
    cerrarTicketsCreadosResueltos() {
      this.$emit('cerrar-tickets-creados-resueltos');
    },
    
    cerrarTiempoPromedio() {
      this.$emit('cerrar-tiempo-promedio');
    },
    
    cerrarTicketsPorTecnico() {
      this.$emit('cerrar-tickets-por-tecnico');
    },
    
    cerrarTicketsPorArea() {
      this.$emit('cerrar-tickets-por-area');
    },
    
    cerrarProblemasComunes() {
      this.$emit('cerrar-problemas-comunes');
    },

    cerrarEstadisticasPreventivas() {
      this.$emit('cerrar-estadisticas-preventivas');
    },
    
    //  MÉTODOS DE COMUNICACIÓN CON BACKEND 
    // Cargar estadísticas generales desde el backend
    async cargarEstadisticasDesdeBackend(filtros = {}) {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/estadisticas',
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          params: {
            fechaInicio: filtros.fechaInicio || undefined,
            fechaFin: filtros.fechaFin || undefined,
            area: filtros.area || undefined,
            tecnico: filtros.tecnico || undefined,
            excluirGestionInformacion: true // Excluir tickets de gestión de información
          }
        });
        
        const data = response.data || {};

        if (data && data.error) {
          console.warn('Backend devolvió error de estadísticas:', data.mensaje || data.error);
          this.$emit('mostrar-notificacion', data.mensaje || 'No se pudieron obtener las estadísticas');
          return;
        }

        const stats = data.estadisticas || data;
        const porEstado = this.normalizarMapa(stats.porEstado);
        const porArea = this.normalizarMapa(stats.porArea);
        const porAreaResueltos = this.normalizarMapa(stats.porAreaResueltos);
        const porAreaPendientes = this.normalizarMapa(stats.porAreaPendientes);
        const porPrioridad = this.normalizarMapa(stats.porPrioridad);

        // Totales generales
        this.ticketsCreados = Number(stats.totalTickets ?? stats.ticketsCreados) || 0;
        const ticketsResueltos = this.obtenerValorMapa(porEstado, ['RESUELTO', 'CERRADO']) || Number(stats.ticketsResueltos) || 0;
        this.ticketsResueltos = ticketsResueltos;
        const pendientesCalculados = this.obtenerValorMapa(porEstado, ['PENDIENTE', 'EN_PROCESO', 'ASIGNADO', 'PENDIENTES']);
        const pendientes = pendientesCalculados || Math.max(this.ticketsCreados - ticketsResueltos, 0);
        this.ticketsPendientes = pendientes;
        this.tasaResolucion = this.calcularTasaResolucion(this.ticketsResueltos, this.ticketsCreados);
        this.actualizarDatosGraficoBasico(porEstado);

        const tiempoPromedio = stats.tiempoPromedioResolucion || stats.tiempoPromedio || {};
        this.tiempoPromedioGeneral = this.formatearHoras(tiempoPromedio.horas);
        this.tiempoPromedioHoras = Number(stats.tiempoPromedioHoras ?? tiempoPromedio.horas ?? 0);
        this.totalTicketsProcesados = Number(stats.totalTicketsProcesados ?? stats.ticketsResueltos ?? this.ticketsResueltos) || 0;
        
        // Datos por prioridad - asegurar que se obtengan correctamente
        this.ticketsAlta = this.obtenerValorMapa(porPrioridad, ['ALTA']);
        this.ticketsMedia = this.obtenerValorMapa(porPrioridad, ['MEDIA']);
        this.ticketsBaja = this.obtenerValorMapa(porPrioridad, ['BAJA']);

        // Para el dictamen mensual, usar directamente los valores del backend
        this.tiempoAltaHoras = Number(stats.tiempoAlta ?? tiempoPromedio.tiempoAlta ?? 0) || 0;
        this.tiempoMediaHoras = Number(stats.tiempoMedia ?? tiempoPromedio.tiempoMedia ?? 0) || 0;
        this.tiempoBajaHoras = Number(stats.tiempoBaja ?? tiempoPromedio.tiempoBaja ?? 0) || 0;

        this.tiempoAlta = this.formatearHoras(this.tiempoAltaHoras);
        this.tiempoMedia = this.formatearHoras(this.tiempoMediaHoras);
        this.tiempoBaja = this.formatearHoras(this.tiempoBajaHoras);

        // Datos por área
        this.ticketsBiomedica = this.obtenerValorMapa(porArea, ['BIOMEDICA', 'BIOMÉDICA']);
        this.ticketsTecnologias = this.obtenerValorMapa(porArea, ['TECNOLOGIAS', 'TECNOLOGÍAS', 'TECNOLOGIA', 'TECNOLOGÍA']);
        this.ticketsMantenimiento = this.obtenerValorMapa(porArea, ['MANTENIMIENTO']);
            
        const totalAreas = this.ticketsBiomedica + this.ticketsTecnologias + this.ticketsMantenimiento;
        this.porcentajeBiomedica = this.calcularPorcentaje(this.ticketsBiomedica, totalAreas);
        this.porcentajeTecnologias = this.calcularPorcentaje(this.ticketsTecnologias, totalAreas);
        this.porcentajeMantenimiento = this.calcularPorcentaje(this.ticketsMantenimiento, totalAreas);

            await this.calcularIndicadoresDiarios(filtros);

        const tiempoPorAreaMap = this.normalizarMapaGenerico(stats.tiempoPorArea);
        this.tiempoPorArea = Object.keys(tiempoPorAreaMap).map(areaKey => {
          const info = tiempoPorAreaMap[areaKey] || {};
          return {
            nombre: this.formatearNombreArea(areaKey),
            tiempo: this.obtenerTiempoDesdeObjeto(info),
            porcentaje: Number(info.porcentaje) || 0
          };
        });

        this.actualizarAreasDetalladas({
          ...stats,
          porArea,
          porAreaResueltos,
          porAreaPendientes,
          porEstado,
          porPrioridad,
          tiempoPorArea: tiempoPorAreaMap
        });

          if (this.showTicketsPorArea) {
            await this.generarDatosGraficaMensualAreas();
          }
          
          console.log('Estadísticas cargadas desde el backend');
      } catch (error) {
        console.error('Error al cargar estadísticas desde backend:', error);
        this.$emit('mostrar-notificacion', 'Error al cargar estadísticas');
      }
    },
    
    // Cargar estadísticas de técnicos desde el backend
    async cargarEstadisticasTecnicosDesdeBackend() {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/listarTecnicos',
          method: 'GET'
        });
        
        if (response.data && response.data.root) {
          this.totalTecnicos = response.data.root.length;
          
          // Obtener estadísticas de cada técnico
          const tecnicosConStats = [];
          let totalAtendidos = 0;
          
          for (const tecnico of response.data.root) {
            try {
              // Obtener estadísticas individuales del técnico
              const statsResponse = await this.$http({
                url: `/mantenimiento/tickets/estadisticasTecnico/${tecnico.id}`,
                method: 'GET',
                headers: {
                  'Cache-Control': 'no-cache, no-store, must-revalidate',
                  'Pragma': 'no-cache',
                  'Expires': '0'
                }
              });
              
              const data = statsResponse.data || {};

              if ((data.success === false) || data.error) {
                console.warn(`Backend devolvió error de estadísticas para técnico ${tecnico.id}:`, data.mensaje || data.error);
                continue;
              }

              const stats = data.root || data.estadisticas || data;
                
                const iniciales = (tecnico.nombre || tecnico.nombres || '')
                  .split(' ')
                  .map(n => n.charAt(0))
                  .slice(0, 2)
                  .join('');
                
              let atendidos = Number(
                stats.ticketsAtendidos ??
                stats.ticketsAsignados ??
                stats.total ??
                0
              ) || 0;

              let resueltos = Number(
                stats.ticketsResueltos ??
                stats.resueltos ??
                stats.ticketsCerrados ??
                0
              ) || 0;

              let pendientes = Number(
                stats.ticketsPendientes ??
                stats.pendientes ??
                (atendidos - resueltos) ??
                0
              ) || 0;

              // Merge/fallback con conteo local usando tickets prop (cubre asignación por ID y por equipo)
              const local = this.contarTicketsLocalParaTecnico(tecnico);
              if (local.atendidos > atendidos) {
                atendidos = local.atendidos;
              }
              if (local.resueltos > resueltos) {
                resueltos = local.resueltos;
              }
              if (local.pendientes > pendientes) {
                pendientes = local.pendientes;
              }

              const eficienciaCalculada = this.calcularTasaResolucion(resueltos, atendidos);
              const eficiencia = Number(
                stats.eficiencia ??
                stats.tasaResolucion ??
                eficienciaCalculada
              ) || 0;

              const tiempoPromedio = this.formatearHoras(
                stats.tiempoPromedio ??
                stats.tiempoPromedioResolucion ??
                stats.promedioHoras ??
                stats.promedioTiempo
              );
                
                tecnicosConStats.push({
                  id: tecnico.id,
                  nombre: tecnico.nombre || `${tecnico.nombres} ${tecnico.apellidoPaterno || ''}`.trim(),
                  iniciales: iniciales,
                atendidos,
                resueltos,
                pendientes,
                tiempoPromedio,
                eficiencia: Number(eficiencia.toFixed ? eficiencia.toFixed(1) : eficiencia)
                });
                
                totalAtendidos += atendidos;
            } catch (error) {
              console.warn(`No se pudieron cargar estadísticas del técnico ${tecnico.id}`, error);
            }
          }
          
          this.tecnicosData = tecnicosConStats;
          this.totalTicketsAtendidos = totalAtendidos;
          this.promedioPorTecnico = this.totalTecnicos > 0 
            ? (totalAtendidos / this.totalTecnicos).toFixed(1) 
            : 0;
          
          console.log(`Estadísticas de ${tecnicosConStats.length} técnicos cargadas`);
        }
      } catch (error) {
        console.error('Error al cargar técnicos:', error);
        this.$emit('mostrar-notificacion', 'Error al cargar estadísticas de técnicos');
      }
    },
    
    // Cargar problemas más comunes desde el backend
    async cargarProblemasComunes() {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/problemasComunes',
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          params: {
            fechaInicio: this.fechaInicio || undefined,
            fechaFin: this.fechaFin || undefined
          }
        });
        
        const data = response.data || {};

        if (data && data.error) {
          console.warn('Backend devolvió error de problemas comunes:', data.mensaje || data.error);
          this.$emit('mostrar-notificacion', data.mensaje || 'No se pudieron obtener los problemas comunes');
          return;
        }

        const problemas = data.problemas || data.root || [];
        this.problemasComunes = problemas
          .filter(p => p != null) // Filtrar nulls
          .map((problema, index) => ({
          id: index + 1,
          descripcion: problema.descripcion || problema.problema || problema.asunto || 'Sin descripción',
          frecuencia: Number(problema.frecuencia || problema.conteo || problema.total || 0),
          porcentaje: Number(problema.porcentaje || 0),
          area: problema.area || problema.solicitudArea || 'No especificada',
          prioridad: problema.prioridad || 'Media',
          tendencia: Number(problema.tendencia || 0)
        }));

        this.totalProblemasAnalizados = Number(data.totalAnalizados || data.total || this.problemasComunes.reduce((sum, p) => sum + p.frecuencia, 0));
        
        console.log('Problemas comunes cargados:', this.problemasComunes.length, 'tipos de problemas');
      } catch (error) {
        console.error('Error al cargar problemas comunes:', error);
        this.$emit('mostrar-notificacion', 'Error al cargar problemas comunes');
        // Datos de ejemplo en caso de error
        this.problemasComunes = [
          { id: 1, descripcion: 'Impresora no funciona', frecuencia: 45, porcentaje: 23.5, area: 'Tecnologías', prioridad: 'Media', tendencia: 5 },
          { id: 2, descripcion: 'Equipo biomédico con falla', frecuencia: 38, porcentaje: 19.8, area: 'Biomédica', prioridad: 'Alta', tendencia: -2 },
          { id: 3, descripcion: 'Aire acondicionado dañado', frecuencia: 32, porcentaje: 16.7, area: 'Mantenimiento', prioridad: 'Alta', tendencia: 8 },
          { id: 4, descripcion: 'Internet lento o caído', frecuencia: 28, porcentaje: 14.6, area: 'Tecnologías', prioridad: 'Alta', tendencia: 12 },
          { id: 5, descripcion: 'Software no responde', frecuencia: 25, porcentaje: 13.0, area: 'Tecnologías', prioridad: 'Media', tendencia: -5 }
        ];
        this.totalProblemasAnalizados = 168;
      }
    },
    
    // Método para cambiar período
    cambiarPeriodo(periodoId) {
      this.periodoActivo = periodoId;
      // Aquí se actualizarían los datos según el período seleccionado
      this.actualizarDatosPorPeriodo();
    },
    
    async actualizarDatosPorPeriodo() {
      // Cargar datos desde el backend según el período seleccionado
      await this.cargarEstadisticasDesdeBackend({
        periodo: this.periodoActivo
      });
    },
    
    // Método para obtener clase de eficiencia
    getEfficiencyClass(eficiencia) {
      if (eficiencia >= 90) return 'high';
      if (eficiencia >= 75) return 'medium';
      return 'low';
    },
    
    // Método para obtener clase de tendencia
    getTrendClass(tendencia) {
      if (tendencia > 0) return 'trend-up';    // Azul = aumentó
      if (tendencia < 0) return 'trend-down';  // Rojo = disminuyó
      return 'trend-neutral';                  // Gris = sin cambio
    },
    
    // Método para obtener clase de prioridad
    getPrioridadClass(prioridad) {
      const prioridadNormalizada = prioridad ? prioridad.toString().toLowerCase() : '';
      if (prioridadNormalizada.includes('alta')) return 'priority-high';
      if (prioridadNormalizada.includes('media')) return 'priority-medium';
      if (prioridadNormalizada.includes('baja')) return 'priority-low';
      return 'priority-medium'; // Por defecto
    },
    
    // Método para obtener símbolo de flecha de tendencia
    getTrendArrow(tendencia) {
      if (tendencia > 0) return '↑';
      if (tendencia < 0) return '↓';
      return '→';
    },
    
    // Métodos para filtros de fecha
    async actualizarDatosPorFecha() {
      if (this.fechaInicio && this.fechaFin) {
        console.log('Filtrando por rango de fechas:', this.fechaInicio, 'a', this.fechaFin);
        await this.actualizarEstadisticasPorFecha();
      }
    },
    
    async actualizarDatosPorAño() {
      if (this.añoSeleccionado) {
        console.log('Filtrando por año:', this.añoSeleccionado);
        // Limpiar mes seleccionado cuando cambia el año
        this.mesSeleccionado = '';
        await this.actualizarEstadisticasPorAño();
      }
    },
    
    async actualizarDatosPorMes() {
      if (this.añoSeleccionado && this.mesSeleccionado) {
        console.log('Filtrando por año y mes:', this.añoSeleccionado, this.mesSeleccionado);
        await this.actualizarEstadisticasPorMes();
      }
    },
    
    limpiarFiltros() {
      this.fechaInicio = '';
      this.fechaFin = '';
      this.añoSeleccionado = '';
      this.mesSeleccionado = '';
      this.periodoActivo = 'dia';
      // Restaurar datos originales
      this.actualizarDatosPorPeriodo();
    },
    
    async actualizarEstadisticasPorFecha() {
      // Cargar datos desde el backend según el rango de fechas
      await this.cargarEstadisticasDesdeBackend({
        fechaInicio: this.fechaInicio,
        fechaFin: this.fechaFin
      });
    },
    
    async actualizarEstadisticasPorAño() {
      // Calcular fechas del año seleccionado
      const fechaInicio = `${this.añoSeleccionado}-01-01`;
      const fechaFin = `${this.añoSeleccionado}-12-31`;
      
      await this.cargarEstadisticasDesdeBackend({
        fechaInicio: fechaInicio,
        fechaFin: fechaFin
      });
    },
    
    async actualizarEstadisticasPorMes() {
      // Calcular fechas del mes seleccionado
      const año = this.añoSeleccionado;
      const mes = String(this.mesSeleccionado).padStart(2, '0');
      
      // Primer día del mes
      const fechaInicio = `${año}-${mes}-01`;
      
      // Último día del mes
      const ultimoDia = new Date(año, this.mesSeleccionado, 0).getDate();
      const fechaFin = `${año}-${mes}-${ultimoDia}`;
      
      await this.cargarEstadisticasDesdeBackend({
        fechaInicio: fechaInicio,
        fechaFin: fechaFin
      });
    },
    
    generarDatosGraficoPorFecha() {
      // Los datos ya vienen del backend en cargarEstadisticasDesdeBackend
      console.log('Datos de gráfico actualizados desde backend');
    },
    
    generarDatosGraficoPorMes() {
      // Los datos ya vienen del backend en cargarEstadisticasDesdeBackend
      console.log('Datos de gráfico actualizados desde backend');
    },
    
    // Generar datos para gráfica mensual de áreas (últimos 6 meses)
    async generarDatosGraficaMensualAreas() {
      try {
        const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                       'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        
        const ticketsPorMes = [];
        const fechaActual = new Date();
        
        // Obtener datos de los últimos 6 meses
        for (let i = 5; i >= 0; i--) {
          const fecha = new Date(fechaActual.getFullYear(), fechaActual.getMonth() - i, 1);
          const año = fecha.getFullYear();
          const mesNum = fecha.getMonth() + 1;
          const mesNombre = meses[fecha.getMonth()];
          const etiqueta = `${mesNombre} ${año}`;
          
          // Calcular primer y último día del mes
          const fechaInicio = `${año}-${String(mesNum).padStart(2, '0')}-01`;
          const ultimoDia = new Date(año, mesNum, 0).getDate();
          const fechaFin = `${año}-${String(mesNum).padStart(2, '0')}-${ultimoDia}`;
          
          // Obtener estadísticas del mes
          const response = await this.$http({
            url: '/mantenimiento/tickets/estadisticas',
            method: 'GET',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
            params: {
              fechaInicio: fechaInicio,
              fechaFin: fechaFin
            }
          });
          
          const data = response.data || {};
          if (!data.error) {
            const stats = data.estadisticas || data;
            const porArea = this.normalizarMapa(stats.porArea);

            const biomedica = this.obtenerValorMapa(porArea, ['BIOMEDICA', 'BIOMÉDICA']);
            const tecnologias = this.obtenerValorMapa(porArea, ['TECNOLOGIAS', 'TECNOLOGÍAS', 'TECNOLOGIA', 'TECNOLOGÍA']);
            const mantenimiento = this.obtenerValorMapa(porArea, ['MANTENIMIENTO']);
            
            ticketsPorMes.push({
              mes: mesNombre,
              etiqueta,
              biomedica,
              tecnologias,
              mantenimiento,
              total: biomedica + tecnologias + mantenimiento
            });
          } else {
            ticketsPorMes.push({
              mes: mesNombre,
              etiqueta,
              biomedica: 0,
              tecnologias: 0,
              mantenimiento: 0,
              total: 0
            });
          }
        }
        
        this.ticketsPorMes = ticketsPorMes;
        console.log('Datos mensuales por área cargados');
      } catch (error) {
        console.error('Error al generar datos de gráfica mensual:', error);
      }
    },
    
    // Calcular indicadores diarios y tendencias por área
    async calcularIndicadoresDiarios(filtros) {
      try {
        // Determinar rango de días
        let diasEnRango = 30; // Por defecto último mes
        
        if (filtros.fechaInicio && filtros.fechaFin) {
          const inicio = new Date(filtros.fechaInicio);
          const fin = new Date(filtros.fechaFin);
          diasEnRango = Math.ceil((fin - inicio) / (1000 * 60 * 60 * 24));
        }
        
        if (diasEnRango <= 0) diasEnRango = 1;
        
        // Calcular promedios diarios
        this.ticketsBiomedicaDiario = (this.ticketsBiomedica / diasEnRango).toFixed(1);
        this.ticketsTecnologiasDiario = (this.ticketsTecnologias / diasEnRango).toFixed(1);
        this.ticketsMantenimientoDiario = (this.ticketsMantenimiento / diasEnRango).toFixed(1);
        
        // Calcular tendencias (comparar con periodo anterior)
        // Por simplicidad, usar valores aleatorios o calcular comparando periodos
        // En producción, esto debería comparar con el mismo periodo anterior
        this.ticketsBiomedicaTendencia = 0;
        this.ticketsTecnologiasTendencia = 0;
        this.ticketsMantenimientoTendencia = 0;
        
      } catch (error) {
        console.error('Error al calcular indicadores diarios:', error);
      }
    },
    
    // Actualizar áreas detalladas con datos del backend
    calcularPorcentaje(valor, total) {
      if (!total) {
        return 0;
      }
      const porcentaje = (Number(valor || 0) / Number(total)) * 100;
      return Number.isFinite(porcentaje) ? Number(porcentaje.toFixed(1)) : 0;
    },
    formatearHoras(valor) {
      if (valor === null || typeof valor === 'undefined') {
        return '0 horas';
      }
      const numero = Number(valor);
      if (Number.isFinite(numero)) {
        if (numero > 0 && numero < 1) {
          const minutos = Math.max(1, Math.round(numero * 60));
          return `${minutos} minutos`;
        }
        const formateado = numero % 1 === 0 ? numero : Number(numero.toFixed(2));
        return `${formateado} horas`;
      }
      if (typeof valor === 'string' && valor.trim() !== '') {
        return valor;
      }
      return '0 horas';
    },
    normalizarMapa(mapa = {}) {
      if (!mapa || typeof mapa !== 'object') {
        return {};
      }
      return Object.keys(mapa).reduce((acc, llave) => {
        if (llave === null || typeof llave === 'undefined') {
          return acc;
        }
        const clave = llave.toString().trim().toUpperCase();
        const valor = mapa[llave];
        if (valor !== null && typeof valor === 'object' && !Array.isArray(valor)) {
          acc[clave] = valor;
        } else if (Number.isFinite(Number(valor))) {
          acc[clave] = Number(valor);
        } else {
          acc[clave] = Number(valor) || 0;
        }
        return acc;
      }, {});
    },
    normalizarMapaGenerico(mapa = {}) {
      if (!mapa || typeof mapa !== 'object') {
        return {};
      }
      return Object.keys(mapa).reduce((acc, llave) => {
        if (llave === null || typeof llave === 'undefined') {
          return acc;
        }
        const clave = llave.toString().trim().toUpperCase();
        acc[clave] = mapa[llave];
        return acc;
      }, {});
    },
    obtenerValorMapa(mapa = {}, llaves = []) {
      if (!mapa || typeof mapa !== 'object' || !Array.isArray(llaves)) {
        return 0;
      }
      for (const llave of llaves) {
        if (!llave) {
          continue;
        }
        const clave = llave.toString().trim().toUpperCase();
        if (Object.prototype.hasOwnProperty.call(mapa, clave)) {
          const valor = mapa[clave];
          if (typeof valor === 'number') {
            return valor;
          }
          if (typeof valor === 'string') {
            const numero = Number(valor);
            if (!Number.isNaN(numero)) {
              return numero;
            }
          }
          if (valor && typeof valor === 'object') {
            if (typeof valor.cantidad !== 'undefined') {
              return Number(valor.cantidad) || 0;
            }
            if (typeof valor.total !== 'undefined') {
              return Number(valor.total) || 0;
            }
            if (typeof valor.valor !== 'undefined') {
              return Number(valor.valor) || 0;
            }
          }
        }
      }
      return 0;
    },
    obtenerObjetoMapa(mapa = {}, llaves = []) {
      if (!mapa || typeof mapa !== 'object' || !Array.isArray(llaves)) {
        return null;
      }
      for (const llave of llaves) {
        if (!llave) {
          continue;
        }
        const clave = llave.toString().trim().toUpperCase();
        if (Object.prototype.hasOwnProperty.call(mapa, clave)) {
          return mapa[clave];
        }
      }
      return null;
    },
    obtenerTiempoDesdeObjeto(info) {
      if (!info || typeof info !== 'object') {
        return '0 horas';
      }
      if (info.tiempo) {
        return info.tiempo;
      }
      if (typeof info.horas !== 'undefined') {
        return this.formatearHoras(info.horas);
      }
      if (typeof info.promedio !== 'undefined') {
        return this.formatearHoras(info.promedio);
      }
      return '0 horas';
    },
    calcularTasaResolucion(resueltos, total) {
      if (!total) {
        return 0;
      }
      const porcentaje = (Number(resueltos || 0) / Number(total)) * 100;
      return Number.isFinite(porcentaje) ? Number(porcentaje.toFixed(1)) : 0;
    },
    actualizarDatosGraficoBasico(porEstado = {}) {
      const total = Number(this.ticketsCreados) || 0;
      const resueltos = this.obtenerValorMapa(porEstado, ['RESUELTO', 'CERRADO']) || Number(this.ticketsResueltos) || 0;
      const pendientesMapa = this.obtenerValorMapa(porEstado, ['PENDIENTE', 'PENDIENTES', 'EN_PROCESO', 'ASIGNADO']);
      const pendientes = pendientesMapa || Math.max(total - (resueltos || 0), 0);

      this.datosGrafico = [
        {
          etiqueta: 'Asignados',
          valor: total,
          className: 'created',
          detalles: [
            `Tickets asignados: ${total}`
          ]
        },
        {
          etiqueta: 'Resueltos',
          valor: resueltos,
          className: 'resolved',
          detalles: [
            `Tickets resueltos: ${resueltos}`,
            `Efectividad: ${this.tasaResolucion}%`
          ]
        },
        {
          etiqueta: 'Pendientes',
          valor: pendientes,
          className: 'pending',
          detalles: [
            `Tickets pendientes: ${pendientes}`
          ]
        }
      ];
    },
    formatearNombreArea(clave) {
      if (!clave) {
        return '';
      }
      const mapa = {
        BIOMEDICA: 'Biomédica',
        'BIOMÉDICA': 'Biomédica',
        TECNOLOGIAS: 'Tecnologías',
        'TECNOLOGÍAS': 'Tecnologías',
        TECNOLOGIA: 'Tecnología',
        'TECNOLOGÍA': 'Tecnología',
        MANTENIMIENTO: 'Mantenimiento'
      };
      const claveNormalizada = clave.toString().trim().toUpperCase();
      if (mapa[claveNormalizada]) {
        return mapa[claveNormalizada];
      }
      return claveNormalizada.charAt(0) + claveNormalizada.slice(1).toLowerCase();
    },
    actualizarAreasDetalladas(stats) {
      if (!stats) {
        this.areasDetalladas = [];
        return;
      }

      const porArea = this.normalizarMapa(stats.porArea);
      const porAreaResueltos = this.normalizarMapa(stats.porAreaResueltos || stats.porAreaResuelto);
      const porAreaPendientes = this.normalizarMapa(stats.porAreaPendientes || stats.porAreaPendiente);
      const tiempoPorArea = this.normalizarMapaGenerico(stats.tiempoPorArea);

      const areasReferencia = [
        { keys: ['BIOMEDICA', 'BIOMÉDICA'], icono: '🔬', clase: 'biomedica' },
        { keys: ['TECNOLOGIAS', 'TECNOLOGÍAS', 'TECNOLOGIA', 'TECNOLOGÍA'], icono: '💻', clase: 'tecnologias' },
        { keys: ['MANTENIMIENTO'], icono: '🔧', clase: 'mantenimiento' }
      ];

      this.areasDetalladas = areasReferencia.map(({ keys, icono, clase }) => {
        const nombre = this.formatearNombreArea(keys[0]);
        const tiempoInfo = this.obtenerObjetoMapa(tiempoPorArea, keys);

        return {
          nombre,
          icono,
          clase,
          total: this.obtenerValorMapa(porArea, keys),
          resueltos: this.obtenerValorMapa(porAreaResueltos, keys),
          pendientes: this.obtenerValorMapa(porAreaPendientes, keys),
          tiempoPromedio: this.obtenerTiempoDesdeObjeto(tiempoInfo)
        };
      });
    },

    // ==========================================
    // MÉTODOS PARA DESCARGAR REPORTES PDF
    // ==========================================

    async descargarReporteTickets() {
      await this.descargarReportePDF('generarReporteTicketsCreadosResueltos', 'reporte-tickets');
    },

    async descargarReporteTiempoPromedio() {
      await this.descargarReportePDF('generarReporteTiempoPromedio', 'reporte-tiempos');
    },

    // Nuevo método para generar dictamen mensual
    async descargarDictamenMensual() {
      try {
        this.$emit('mostrar-notificacion', 'Generando dictamen mensual...');

        const params = {
          fechaInicio: this.fechaInicio || undefined,
          fechaFin: this.fechaFin || undefined,
          tipoReporte: 'dictamen_mensual', // Parámetro específico para dictamen
          incluirTiempoResolucion: true
        };

        const response = await this.$http({
          url: '/mantenimiento/tickets/generarReporteTiempoPromedio', // Usar endpoint existente
          method: 'GET',
          params: params,
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        });

        // Crear blob y descargar
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        const fechaInicioStr = this.fechaInicio || 'inicio';
        const fechaFinStr = this.fechaFin || 'actual';
        const nombreArchivo = `dictamen-mensual-${fechaInicioStr}-${fechaFinStr}.pdf`;

        link.href = url;
        link.setAttribute('download', nombreArchivo);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$emit('mostrar-notificacion', 'Dictamen mensual descargado exitosamente');
      } catch (error) {
        console.error('Error al descargar dictamen mensual:', error);
        this.$emit('mostrar-notificacion', 'Error al descargar el dictamen mensual');
      }
    },

    async descargarReporteTecnicos() {
      await this.descargarReportePDF('generarReporteTicketsPorTecnico', 'reporte-tecnicos');
    },

    async descargarReporteAreas() {
      await this.descargarReportePDF('generarReporteTicketsPorArea', 'reporte-areas');
    },

    async descargarReporteProblemas() {
      await this.descargarReportePDF('generarReporteProblemasComunes', 'reporte-problemas');
    },

    async descargarReportePreventivo() {
      await this.descargarReportePDF('generarReporteEstadisticasPreventivas', 'reporte-preventivo');
    },

    async descargarReportePDF(endpoint, nombreBase) {
      try {
        this.$emit('mostrar-notificacion', 'Generando reporte PDF...');

        const params = {
          fechaInicio: this.fechaInicio || undefined,
          fechaFin: this.fechaFin || undefined
        };

        const response = await this.$http({
          url: `/mantenimiento/tickets/${endpoint}`,
          method: 'GET',
          params: params,
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        });

        // Validar que la respuesta sea un PDF válido
        const blob = response.data;

        // Verificar el tipo de contenido
        const contentType = response.headers['content-type'];
        if (!contentType || (!contentType.includes('application/pdf') && !contentType.includes('application/octet-stream'))) {
          // Intentar leer el blob como texto para ver si es un mensaje de error
          const errorText = await blob.text();
          let errorMessage = 'El servidor no generó un PDF válido';
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch (e) {
            // No es JSON, usar el texto directamente si es corto
            if (errorText && errorText.length < 200) {
              errorMessage = errorText;
            }
          }
          this.$emit('mostrar-notificacion', `Error: ${errorMessage}`);
          console.error('Error al descargar reporte - respuesta no es PDF:', errorMessage);
          return;
        }

        // Verificar que el blob tenga contenido válido (los PDFs comienzan con %PDF)
        const arrayBuffer = await blob.slice(0, 8).arrayBuffer();
        const header = new Uint8Array(arrayBuffer);
        const pdfHeader = '%PDF';
        let isValidPdf = true;
        for (let i = 0; i < pdfHeader.length; i++) {
          if (header[i] !== pdfHeader.charCodeAt(i)) {
            isValidPdf = false;
            break;
          }
        }

        if (!isValidPdf) {
          // Intentar leer como texto para ver el mensaje de error
          const errorText = await blob.text();
          let errorMessage = 'El archivo generado no es un PDF válido';
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch (e) {
            if (errorText && errorText.length < 200) {
              errorMessage = errorText;
            }
          }
          this.$emit('mostrar-notificacion', `Error: ${errorMessage}`);
          console.error('Error al descargar reporte - archivo no es PDF válido:', errorMessage);
          return;
        }

        // Crear blob y descargar
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');

        const fechaInicioStr = this.fechaInicio || 'inicio';
        const fechaFinStr = this.fechaFin || 'actual';
        const nombreArchivo = `${nombreBase}-${fechaInicioStr}-${fechaFinStr}.pdf`;

        link.href = url;
        link.setAttribute('download', nombreArchivo);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$emit('mostrar-notificacion', 'Reporte descargado exitosamente');
      } catch (error) {
        console.error('Error al descargar reporte:', error);
        this.$emit('mostrar-notificacion', 'Error al descargar el reporte PDF');
      }
    }
  }
}
</script>

<style scoped>
/* Estilos para dashboard de estadísticas */
.stats-dashboard {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Paleta institucional sin verdes */
  --color-primary: #1f4f7a;
  --color-primary-dark: #143552;
  --color-navy: #1c2f48;
  --color-slate: #4f6d7a;
  --color-info: #2f6b8a;
  --color-amber: #d17a22;
  --color-amber-dark: #9b5715;
  --color-red: #b33430;
  --color-red-dark: #8a2726;
  --color-bg: #f6f8fa;
  --color-bg-alt: #eef1f5;
  --color-border: #e3e7ed;
  background: linear-gradient(180deg, var(--color-bg) 0%, var(--color-bg-alt) 100%);
  z-index: 1000;
  overflow-y: auto;
  font-family: 'Helvetica LT Std Condensed Oblique', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.stats-view {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
}

.stats-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 95vw;
  max-width: 1400px;
  margin: 20px auto;
  overflow: hidden;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px 40px;
  border-bottom: 1px solid var(--color-border);
  background: linear-gradient(180deg, #ffffff 0%, var(--color-bg) 100%);
}

.stats-header h2 {
  margin: 0;
  color: #333;
  font-size: 2rem;
  font-weight: 700;
  font-family: 'Helvetica LT Std Black Condensed Oblique', sans-serif;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 5px;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: #e9ecef;
  color: #333;
}

.stats-content {
  padding: 40px;
}

/* Filtros de período */
.period-filters {
  display: flex;
  gap: 10px;
  margin-bottom: 25px;
  justify-content: center;
}

.period-btn {
  padding: 8px 16px;
  border: 2px solid #e9ecef;
  background: white;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  color: #666;
}

.period-btn.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.period-btn:hover:not(.active) {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* Filtros de fecha */
.date-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.date-filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-filter-group label {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.date-input,
.year-select,
.month-select {
  padding: 10px 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.date-input:focus,
.year-select:focus,
.month-select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(31, 79, 122, 0.15);
}

.date-input:hover,
.year-select:hover,
.month-select:hover {
  border-color: var(--color-primary);
}

.month-select:disabled {
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.clear-filters-btn {
  grid-column: 1 / -1;
  justify-self: center;
  background: var(--color-slate);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.clear-filters-btn:hover {
  background: #3f5965;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(79, 109, 122, 0.3);
}

/* Estadísticas principales */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

.stat-card {
  padding: 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
}

/* COLORES SEMÁNTICOS: según el resultado, no el área */
.stat-card.created {
  background: linear-gradient(135deg, var(--color-info) 0%, var(--color-navy) 100%); /* Azul institucional */
  color: white;
}

.stat-card.resolved {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); /* Azul petróleo */
  color: white;
}

.stat-card.pending {
  background: linear-gradient(135deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Ámbar institucional */
  color: white;
}

.stat-card.rate {
  background: linear-gradient(135deg, #25384d 0%, #1a293a 100%); /* Azul profundo */
  color: white;
}

.stat-number {
  font-size: 3.5rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* Gráfico de barras */
.chart-container h4 {
  margin: 0 0 20px 0;
  color: #333;
  text-align: center;
  font-size: 1.2rem;
  font-family: 'Helvetica LT Std Condensed Oblique', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.bar-chart {
  display: flex;
  align-items: end;
  gap: 15px;
  height: 200px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.bar-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
}

.bar-label {
  margin-bottom: 10px;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.bar-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 160px;
  width: 100%;
  justify-content: flex-end;
}

.bar-created,
.bar-resolved {
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  position: relative;
  transition: height 0.35s ease, background 0.3s ease;
  overflow: hidden;
}

.bar-created {
  background: linear-gradient(180deg, var(--color-info) 0%, var(--color-navy) 100%); /* Azul institucional */
}

.bar-resolved {
  background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); /* Azul petróleo */
}

.bar-created.empty,
.bar-resolved.empty {
  background: rgba(0, 0, 0, 0.08);
}

.bar-value {
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
  padding: 2px 4px;
}

.bar-metrics {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.8rem;
  color: #4a4a4a;
  text-align: center;
}

.bar-metrics .metric {
  display: block;
}

/* estilos para scatter plots */
.scatter-chart,
.priority-scatter-chart {
  position: relative;
  height: 200px;
  width: 100%;
  background: #fff;
  border: 1px solid #ddd;
  margin-top: 15px;
  padding: 18px 22px 30px 38px;
  box-sizing: border-box;
  overflow: hidden;
}

.scatter-point,
.priority-scatter-point {
  position: absolute;
  transform: translateX(-50%);
  text-align: center;
}

.chart-axis-label {
  font-size: 12px;
  color: #666;
  line-height: 1;
}

.chart-axis-label.axis-x {
  position: absolute;
  left: 50%;
  bottom: 6px;
  transform: translateX(-50%);
}

.chart-axis-label.axis-y {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translate(-50%, -50%) rotate(-90deg);
  transform-origin: center;
}
.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}
.dot-created { background: linear-gradient(180deg, var(--color-info) 0%, var(--color-navy) 100%); }
.dot-resolved { background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); }
.dot-pending { background: #ccc; }
.dot-high { background: #ff7961; }
.dot-medium { background: #ffb74d; }
.dot-low { background: #81c784; }
.scatter-label {
  font-size: 0.7rem;
  margin-top: 4px;
}

.bar-metrics .metric-created {
  color: var(--color-navy);
  font-weight: 600;
}

.bar-metrics .metric-resolved {
  color: var(--color-primary-dark);
  font-weight: 600;
}

.bar-metrics .metric-total {
  color: #555;
  font-weight: 500;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.legend-color.created {
  background: #2f6b8a; /* Azul institucional = Creados */
}

.legend-color.resolved {
  background: #1f4f7a; /* Azul petróleo = Resueltos */
}

/* Tiempo promedio */
.time-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.time-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.time-card:hover {
  transform: translateY(-5px);
}

/* COLORES SEMÁNTICOS: según rapidez de resolución */
.time-card.general {
  background: linear-gradient(135deg, #7b8e9e 0%, var(--color-slate) 100%); /* Neutro institucional */
  color: white;
}

.time-card.high {
  background: linear-gradient(135deg, var(--color-red) 0%, var(--color-red-dark) 100%); /* Rojo institucional */
  color: white;
}

.time-card.medium {
  background: linear-gradient(135deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Ámbar institucional */
  color: white;
}

.time-card.low {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); /* Azul petróleo */
  color: white;
}

.time-icon {
  font-size: 2.5rem;
  margin-right: 20px;
}

.time-info {
  flex: 1;
}

.time-label {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
}

.time-value {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
}

.time-tickets {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* Gráfico de tiempo por área */
.time-chart {
  margin-bottom: 30px;
}

.time-chart h4 {
  margin: 0 0 20px 0;
  color: #333;
  text-align: center;
  font-size: 1.2rem;
}

.time-bars {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.time-bar-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.time-bar-label {
  min-width: 120px;
  font-weight: 600;
  color: #333;
}

.time-bar-container {
  flex: 1;
  height: 30px;
  background: #e9ecef;
  border-radius: 15px;
  position: relative;
  overflow: hidden;
}

.time-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #7b8e9e 0%, var(--color-slate) 100%); /* Neutro institucional */
  border-radius: 15px;
  transition: width 0.3s ease;
}

.time-bar-value {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

/* Tabla de técnicos */
.tech-table {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.tech-table table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.tech-table th {
  background: #f8f9fa;
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.tech-table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  color: #555;
}

.tech-table tr:hover {
  background: #f8f9fa;
}

.tech-name {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tech-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7b8e9e 0%, var(--color-slate) 100%); /* Neutral institucional */
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
}

.efficiency {
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.85rem;
}

.efficiency.high {
  background: #d6e4f0;
  color: #1f4f7a;
}

.efficiency.medium {
  background: #fff3cd;
  color: #856404;
}

.efficiency.low {
  background: #f8d7da;
  color: #721c24;
}

/* Estadísticas por área */
.area-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.area-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.area-card:hover {
  transform: translateY(-5px);
}

/* COLORES SEMÁNTICOS: tonos distintos para diferenciar visualmente, no por área específica */
.area-card.biomedica {
  background: linear-gradient(135deg, #3b5f8c 0%, #274b73 100%); /* Azul acero */
  color: white;
}

.area-card.tecnologias {
  background: linear-gradient(135deg, #1c2f48 0%, #111d2c 100%); /* Azul marino */
  color: white;
}

.area-card.mantenimiento {
  background: linear-gradient(135deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Bronce */
  color: white;
}

.area-icon {
  font-size: 2.5rem;
  margin-right: 20px;
}

.area-info {
  flex: 1;
}

.area-name {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
}

.area-tickets {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 5px;
}

.area-percentage {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* Indicador diario en cards de área */
.area-daily-indicator {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.85rem;
}

.daily-label {
  font-weight: 600;
  opacity: 0.9;
}

.daily-value {
  font-size: 1rem;
  font-weight: bold;
  margin-top: 2px;
}

.daily-trend {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.8rem;
  margin-top: 5px;
  width: fit-content;
}

.daily-trend.trend-up {
  background: rgba(31, 79, 122, 0.35); /* Azul transparente = Aumentó */
  color: #1d2d40;
}

.daily-trend.trend-down {
  background: rgba(244, 67, 54, 0.3); /* Rojo transparente = Disminuyó */
  color: #fff;
}

.daily-trend.trend-neutral {
  background: rgba(125, 137, 148, 0.3); /* Gris transparente = Sin cambio */
  color: #25313d;
}

/* Gráfico de barras mensuales por área */
.area-monthly-bars-chart {
  margin: 40px 0;
  padding: 30px;
  background: #f8f9fa;
  border-radius: 12px;
}

 .area-monthly-bars-chart .chart-axis-label {
   font-size: 12px;
   color: #666;
   margin: 6px 0;
 }

 .vertical-monthly-chart {
   position: relative;
 }

 .vertical-monthly-chart-bars {
   display: flex;
   gap: 10px;
   align-items: flex-end;
   min-height: 220px;
   padding: 10px 0;
 }

 .vertical-month-group {
   flex: 1;
   min-width: 60px;
   display: flex;
   flex-direction: column;
   align-items: center;
 }

 .vertical-bars {
   width: 100%;
   display: flex;
   align-items: flex-end;
   justify-content: center;
   gap: 4px;
   height: 200px;
 }

 .vertical-bar {
   width: 10px;
   border-radius: 4px 4px 0 0;
   min-height: 2px;
 }

 .vertical-month-label {
   margin-top: 8px;
   font-size: 11px;
   text-align: center;
 }

.area-monthly-bars-chart h4 {
  margin: 0 0 30px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.monthly-chart-container {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.monthly-bar-group {
  display: grid;
  grid-template-columns: 100px 1fr 120px;
  align-items: center;
  gap: 15px;
}

.monthly-label {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
  text-align: right;
}

.monthly-bars {
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: white;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.monthly-bar-item {
  height: 28px;
  background: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.monthly-bar-fill {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 0.8s ease;
  min-width: 30px;
}

.monthly-bar-fill.biomedica {
  background: linear-gradient(90deg, #3b5f8c 0%, #274b73 100%); /* Azul acero */
}

.monthly-bar-fill.tecnologias {
  background: linear-gradient(90deg, #1c2f48 0%, #111d2c 100%); /* Azul marino */
}

.monthly-bar-fill.mantenimiento {
  background: linear-gradient(90deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Bronce */
}

.monthly-bar-value {
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.monthly-total {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
  text-align: center;
  padding: 8px 12px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.monthly-chart-legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 2px solid #dee2e6;
}

.monthly-chart-legend .legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.monthly-chart-legend .legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

/* Gráfico circular */
.pie-chart-container {
  margin-bottom: 30px;
}

.pie-chart-container h4 {
  margin: 0 0 20px 0;
  color: #333;
  text-align: center;
  font-size: 1.2rem;
}

.pie-chart {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: conic-gradient(
    #1f4f7a 0deg 165.6deg,
    #1c2f48 165.6deg 280.8deg,
    #d17a22 280.8deg 360deg
  );
  margin: 0 auto 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pie-chart::before {
  content: '';
  width: 100px;
  height: 100px;
  background: white;
  border-radius: 50%;
  position: absolute;
}

.pie-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.pie-legend .legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pie-legend .legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.pie-legend .legend-color.biomedica {
  background: #3b5f8c; /* Azul acero */
}

.pie-legend .legend-color.tecnologias {
  background: #1c2f48; /* Azul marino */
}

.pie-legend .legend-color.mantenimiento {
  background: #d17a22; /* Bronce */
}

/* Tabla de áreas */
.area-table {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.area-table table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.area-table th {
  background: #f8f9fa;
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.area-table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  color: #555;
}

.area-table tr:hover {
  background: #f8f9fa;
}

.area-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.area-icon-small {
  font-size: 1.2rem;
}

.stats-actions {
  padding: 30px 40px;
  border-top: 1px solid #e0e0e0;
  text-align: right;
  background: #f8f9fa;
}

.stats-actions button {
  background: #6c757d;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
}

.stats-actions button:hover {
  background: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.stats-actions button:active {
  transform: translateY(0);
}

/* Estilos para gráficas de barras por prioridad */
.priority-chart {
  margin-bottom: 40px;
}

.priority-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.priority-bars {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 30px;
  padding: 10px 0;
}

.priority-bar-item.vertical {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 90px;
}

.priority-label {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.priority-bar-box {
  width: 70px;
  height: 200px;
  background: #e9ecef;
  border-radius: 12px;
  position: relative;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  box-shadow: inset 0 0 8px rgba(0,0,0,0.08);
}

.priority-bar-fill {
  width: 100%;
  border-radius: 12px 12px 0 0;
  transition: height 0.8s ease;
  position: relative;
  min-height: 0;
}

.priority-bar-fill.high {
  background: linear-gradient(180deg, var(--color-red) 0%, var(--color-red-dark) 100%);
}

.priority-bar-fill.medium {
  background: linear-gradient(180deg, var(--color-amber) 0%, var(--color-amber-dark) 100%);
}

.priority-bar-fill.low {
  background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
}

.priority-bar-value.vertical {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.priority-bar-meta.vertical {
  font-size: 0.85rem;
  color: #666;
}

/* Estilos para gráficas de barras por técnico */
.tech-chart {
  margin-bottom: 40px;
}

.tech-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.tech-bars {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tech-bar-item {
  display: flex;
  align-items: center;
  gap: 20px;
}

.tech-bar-label {
  min-width: 150px;
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
}

.tech-bar-container {
  flex: 1;
  height: 40px;
  background: #e9ecef;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.tech-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); /* Azul petróleo = Productividad/Rendimiento */
  border-radius: 20px;
  transition: width 0.8s ease;
}

.tech-bar-value {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

/* Estilos para gráficas de barras por área */
.area-bars-chart {
  margin-bottom: 40px;
}

.area-bars-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.area-bars {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.area-bar-item {
  display: flex;
  align-items: center;
  gap: 20px;
}

.area-bar-label {
  min-width: 150px;
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
}

.area-bar-container {
  flex: 1;
  height: 40px;
  background: #e9ecef;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.area-bar-fill {
  height: 100%;
  border-radius: 20px;
  transition: width 0.8s ease;
}

.area-bar-fill.biomedica {
  background: linear-gradient(90deg, #3b5f8c 0%, #274b73 100%); /* Azul acero */
}

.area-bar-fill.tecnologias {
  background: linear-gradient(90deg, #1c2f48 0%, #111d2c 100%); /* Azul marino */
}

.area-bar-fill.mantenimiento {
  background: linear-gradient(90deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Bronce */
}

.area-bar-value {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

/* Gráfico de barras verticales por área */
.area-vertical-bars-chart {
  margin-bottom: 40px;
}

.area-vertical-bars-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.vertical-bars-container {
  display: flex;
  justify-content: space-around;
  align-items: end;
  height: 300px;
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.vertical-bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 120px;
}

.vertical-bar-label {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
  margin-bottom: 15px;
  text-align: center;
}

.vertical-bar-wrapper {
  width: 60px;
  height: 200px;
  background: #e9ecef;
  border-radius: 8px 8px 0 0;
  position: relative;
  display: flex;
  align-items: end;
  margin-bottom: 10px;
}

.vertical-bar-fill {
  width: 100%;
  border-radius: 8px 8px 0 0;
  transition: height 0.8s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 20px;
}

.vertical-bar-fill.biomedica {
  background: linear-gradient(180deg, #3b5f8c 0%, #274b73 100%); /* Azul acero */
}

.vertical-bar-fill.tecnologias {
  background: linear-gradient(180deg, #1c2f48 0%, #111d2c 100%); /* Azul marino */
}

.vertical-bar-fill.mantenimiento {
  background: linear-gradient(180deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Bronce */
}

.vertical-bar-value {
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.vertical-bar-percentage {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
  text-align: center;
}

.vertical-chart-legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
}

.vertical-chart-legend .legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.vertical-chart-legend .legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.vertical-chart-legend .legend-color.biomedica {
  background: #3b5f8c; /* Azul acero */
}

.vertical-chart-legend .legend-color.tecnologias {
  background: #1c2f48; /* Azul marino */
}

.vertical-chart-legend .legend-color.mantenimiento {
  background: #d17a22; /* Bronce */
}

/* Responsive para estadísticas */
@media (max-width: 768px) {
  .stats-container {
    width: 98vw;
    margin: 10px;
  }
  
  .stats-header {
    padding: 20px;
  }
  
  .stats-header h2 {
    font-size: 1.5rem;
  }
  
  .stats-content {
    padding: 20px;
  }
  
  .stats-summary {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .time-stats {
    grid-template-columns: 1fr;
  }
  
  .area-stats {
    grid-template-columns: 1fr;
  }
  
  /* Responsive para gráfica mensual */
  .monthly-bar-group {
    grid-template-columns: 80px 1fr 100px;
    gap: 10px;
  }
  
  .monthly-label {
    font-size: 0.9rem;
  }
  
  .monthly-chart-legend {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }
  
  .bar-chart {
    flex-direction: column;
    height: auto;
    gap: 10px;
  }
  
  .bar-item {
    flex-direction: row;
    height: auto;
  }
  
  .bar-container {
    flex-direction: row;
    height: 30px;
    width: 200px;
  }
  
  .period-filters {
    flex-wrap: wrap;
  }
  
  .date-filters {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 20px;
  }
  
  .clear-filters-btn {
    grid-column: 1;
    justify-self: stretch;
  }
  
  .vertical-bars-container {
    height: 250px;
    padding: 15px;
  }
  
  .vertical-bar-wrapper {
    width: 50px;
    height: 150px;
  }
  
  .vertical-bar-item {
    max-width: 80px;
  }
  
  .vertical-bar-label {
    font-size: 0.9rem;
  }
  
  .vertical-bar-value {
    font-size: 0.8rem;
  }
  
  .vertical-chart-legend {
    gap: 20px;
  }
  
  .priority-bar-item,
  .tech-bar-item,
  .area-bar-item {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .priority-label,
  .tech-bar-label,
  .area-bar-label {
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .stats-header {
    padding: 15px;
  }
  
  .stats-header h2 {
    font-size: 1.2rem;
  }
  
  .stats-content {
    padding: 15px;
  }
  
  .stat-card {
    padding: 20px;
  }
  
  .stat-number {
    font-size: 2.5rem;
  }
  
  .time-card {
    padding: 15px;
  }
  
  .time-icon {
    font-size: 2rem;
    margin-right: 15px;
  }
  
  .time-value {
    font-size: 1.3rem;
  }
  
  .area-card {
    padding: 15px;
  }
  
  .area-icon {
    font-size: 2rem;
    margin-right: 15px;
  }
  
  .area-tickets {
    font-size: 1.5rem;
  }
  
  .stats-actions {
    padding: 20px 15px;
  }
  
  .stats-actions button {
    padding: 12px 20px;
    font-size: 1rem;
  }
  
  .vertical-bars-container {
    height: 200px;
    padding: 10px;
  }
  
  .vertical-bar-wrapper {
    width: 40px;
    height: 120px;
  }
  
  .vertical-bar-item {
    max-width: 60px;
  }
  
  .vertical-bar-label {
    font-size: 0.8rem;
    margin-bottom: 10px;
  }
  
  .vertical-bar-value {
    font-size: 0.7rem;
  }
  
  .vertical-bar-percentage {
    font-size: 0.8rem;
  }
  
  .vertical-chart-legend {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
  
  /* Cards de área más compactas en móvil */
  .area-card {
    padding: 15px;
  }
  
  .area-icon {
    font-size: 2rem;
    margin-right: 15px;
  }
  
  .area-daily-indicator {
    font-size: 0.75rem;
  }
  
  .daily-value {
    font-size: 0.9rem;
  }
  
  /* Gráfica mensual más compacta */
  .area-monthly-bars-chart {
    padding: 20px;
  }
  
  .monthly-bar-group {
    grid-template-columns: 70px 1fr 90px;
  }
  
  .monthly-label {
    font-size: 0.85rem;
  }
  
  .monthly-bar-item {
    height: 24px;
  }
  
  .monthly-bar-value {
    font-size: 0.75rem;
  }
}

/* Estilos para botones de descarga de reportes */
.download-btn {
  background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(31, 79, 122, 0.3);
}

.download-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(31, 79, 122, 0.4);
}

.download-btn:active {
  transform: translateY(0);
}

.dictamen-btn {
  background: linear-gradient(180deg, var(--color-amber) 0%, var(--color-amber-dark) 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(209, 122, 34, 0.3);
}

.dictamen-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(209, 122, 34, 0.4);
}

.dictamen-btn:active {
  transform: translateY(0);
}

.download-icon {
  font-size: 1.2rem;
}

.stats-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .stats-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .stats-actions button,
  .download-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Estilos para Problemas Comunes */
.problemas-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.problemas-chart {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
}

.problemas-bars {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.problema-bar-item {
  display: grid;
  grid-template-columns: 1fr 200px 80px;
  gap: 15px;
  align-items: center;
  padding: 15px;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #007bff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.problema-bar-item:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
}

.problema-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.problema-descripcion {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.problema-meta {
  display: flex;
  gap: 10px;
  align-items: center;
}

.problema-area {
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: #495057;
}

.problema-prioridad {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.problema-prioridad.priority-high {
  background: #f8d7da;
  color: #721c24;
}

.problema-prioridad.priority-medium {
  background: #fff3cd;
  color: #856404;
}

.problema-prioridad.priority-low {
  background: #d4edda;
  color: #155724;
}

.problema-bar-container {
  position: relative;
  height: 35px;
  background: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.problema-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  border-radius: 6px;
  transition: width 0.8s ease;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 10px;
}

.problema-bar-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.problema-frecuencia {
  font-weight: 700;
  color: white;
  font-size: 0.9rem;
}

.problema-porcentaje {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;
}

.problema-tendencia {
  text-align: center;
}

.tendencia {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.tendencia.trend-up {
  background: #d4edda;
  color: #155724;
}

.tendencia.trend-down {
  background: #f8d7da;
  color: #721c24;
}

.tendencia.trend-neutral {
  background: #e2e3e5;
  color: #383d41;
}

.problemas-table {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.problemas-table table {
  width: 100%;
  border-collapse: collapse;
}

.prevent-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 14px;
}

@media (min-width: 980px) {
  .prevent-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.prevent-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  padding: 12px;
  background: #fff;
}

.prevent-card h4 {
  margin: 0 0 10px 0;
  font-size: 0.95rem;
}

.predict-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 14px 0 4px 0;
}

@media (min-width: 980px) {
  .predict-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.predict-card {
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.predict-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 10px 12px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.predict-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.predict-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(209, 122, 34, 0.10);
  font-size: 18px;
}

.predict-title {
  font-weight: 700;
  color: #1c2f48;
  font-size: 0.95rem;
  line-height: 1.1;
}

.predict-subtitle {
  font-size: 0.8rem;
  color: rgba(28, 47, 72, 0.75);
}

.predict-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 62px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.predict-badge-low {
  background: rgba(47, 107, 138, 0.12);
  color: #2f6b8a;
}

.predict-badge-medium {
  background: rgba(209, 122, 34, 0.16);
  color: #9b5715;
}

.predict-badge-high {
  background: rgba(179, 52, 48, 0.14);
  color: #8a2726;
}

.predict-body {
  padding: 12px;
}

.predict-text {
  font-size: 0.92rem;
  color: rgba(28, 47, 72, 0.95);
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 10px;
}

.predict-meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

@media (min-width: 680px) {
  .predict-meta {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.predict-meta-item {
  background: rgba(79, 109, 122, 0.07);
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  padding: 8px 10px;
}

.predict-meta-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(28, 47, 72, 0.70);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.predict-meta-value {
  display: block;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1c2f48;
  margin-top: 2px;
}

.predict-card-low {
  border-left: 4px solid #2f6b8a;
}

.predict-card-medium {
  border-left: 4px solid #d17a22;
}

.predict-card-high {
  border-left: 4px solid #b33430;
}

.prevent-table {
  width: 100%;
  overflow: auto;
}

.prevent-table table {
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;
}

.prevent-table th,
.prevent-table td {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  font-size: 0.86rem;
  vertical-align: top;
}

.prevent-area,
.prevent-problema {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.86);
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.problemas-table th {
  background: #343a40;
  color: white;
  padding: 15px;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
}

.problemas-table td {
  padding: 12px 15px;
  border-bottom: 1px solid #dee2e6;
}

.problemas-table tr:hover {
  background: #f8f9fa;
}

.problema-rank {
  font-weight: 700;
  color: #007bff;
  text-align: center;
  font-size: 1.1rem;
}

.problema-descripcion-cell {
  font-weight: 500;
  color: #2c3e50;
  max-width: 300px;
}

.problema-area-cell {
  color: #6c757d;
}

.problema-frecuencia-cell {
  text-align: center;
}

.porcentaje-chica {
  color: #6c757d;
  font-size: 0.85rem;
  margin-left: 5px;
}

.prioridad-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.prioridad-badge.priority-high {
  background: #f8d7da;
  color: #721c24;
}

.prioridad-badge.priority-medium {
  background: #fff3cd;
  color: #856404;
}

.prioridad-badge.priority-low {
  background: #d4edda;
  color: #155724;
}

.tendencia-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.tendencia-badge.trend-up {
  background: #d4edda;
  color: #155724;
}

.tendencia-badge.trend-down {
  background: #f8d7da;
  color: #721c24;
}

.tendencia-badge.trend-neutral {
  background: #e2e3e5;
  color: #383d41;
}

/* Responsive para problemas comunes */
@media (max-width: 768px) {
  .problema-bar-item {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .problema-bar-container {
    width: 100%;
  }
  
  .problema-tendencia {
    text-align: left;
  }
  
  .problemas-table {
    overflow-x: auto;
  }
  
  .problema-descripcion-cell {
    max-width: 200px;
  }
}
</style> 